import { Fit, Layout, useRive } from '@rive-app/react-canvas-lite'
import { motion } from 'motion/react'
import { use, useEffect, useRef } from 'react'

import { m } from '~/paraglide/messages.js'

import { loadingRewardRive } from '~/assets/rive-files'

export function RewardContent({
  image,
  title,
  description,
  textColor,
  buttonLabel,
  onButtonClick,
}: {
  image: string
  title: string
  description?: string
  textColor: string
  buttonLabel: string
  onButtonClick: () => void
}) {
  const riveFile = use(loadingRewardRive)
  const { RiveComponent, rive } = useRive({
    riveFile,
    autoplay: true,
    artboard: 'reward_reveal',
    stateMachines: ['State Machine 1'],
    layout: new Layout({ fit: Fit.FitHeight }),
  })

  // Set status=3 (tree type) with no fallback
  useEffect(() => {
    if (rive) {
      const inputs = rive.stateMachineInputs('State Machine 1')
      if (inputs) {
        for (const input of inputs) {
          if (input.name === 'status') input.value = 3
          if (input.name === 'repeat') input.value = false
        }
      }
    }
  }, [rive])

  const canClickRef = useRef(false)
  useEffect(() => {
    const timer = setTimeout(() => {
      canClickRef.current = true
    }, 2000)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  return (
    <>
      {/* Main */}
      <div className="flex flex-1 flex-col justify-center gap-6 px-6">
        <motion.p
          className={`text-center text-xl font-semibold ${textColor}`}
          style={{ textShadow: '0 0 15px rgba(255, 255, 255, 0.70)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          {m.claimed_title()}
        </motion.p>

        {/* Reward image */}
        <div className="relative mx-auto flex h-[40vh] w-full max-w-[40vh] items-center justify-center">
          <RiveComponent />
          <motion.img
            src={image}
            className="absolute inset-0 m-auto size-[50%]"
            alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          />
        </div>

        <motion.h1
          className={`text-center text-[30px] font-bold ${textColor}`}
          style={{ textShadow: '0 0 15px rgba(255, 255, 255, 0.70)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          {title}
        </motion.h1>
        {description && (
          <motion.p
            className={`text-center text-[20px] ${textColor}`}
            style={{ textShadow: '0 0 15px rgba(255, 255, 255, 0.70)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            {description}
          </motion.p>
        )}
      </div>

      {/* Footer */}
      <div className="mx-auto w-full max-w-[640px] shrink-0 px-6 pb-12">
        <motion.button
          className="h-[52px] w-full rounded-xl bg-white text-base font-bold text-[#176361]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          onClick={() => {
            if (!canClickRef.current) return
            onButtonClick()
          }}
        >
          {buttonLabel}
        </motion.button>
      </div>
    </>
  )
}
